<template>
  <div class="mt-4 sm:mt-6">
    <!-- Loading State -->
    <LoadingSpinner
      v-if="loading"
      size="sm"
      color="primary"
      text="Cargando grupos..."
    />

    <!-- Empty State -->
    <EmptyState
      v-else-if="groups.length === 0"
      icon="folder"
      title="No hay grupos configurados"
      description="Crea tu primer grupo de modificadores para empezar"
      variant="card"
      size="md"
    />

    <!-- List View -->
    <div
      v-else-if="viewMode === 'list'"
      class="bg-white dark:bg-gray-900 rounded-lg shadow overflow-hidden divide-y divide-gray-200 dark:divide-gray-700"
    >
      <div
        v-for="group in groups"
        :key="group.id"
        class="p-3 sm:p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        <!-- Mobile: Vertical layout -->
        <div class="sm:hidden space-y-3">
          <!-- Header with name and status -->
          <div class="flex items-start justify-between gap-2">
            <div class="flex-1 min-w-0">
              <h3 class="text-sm font-semibold text-gray-900 dark:text-white truncate">
                {{ group.name }}
              </h3>
              <p
                v-if="group.description"
                class="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2"
              >
                {{ group.description }}
              </p>
            </div>
            <div class="flex items-center gap-2 flex-shrink-0">
              <span
                class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                :class="group.is_active ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-200' : 'bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-300'"
              >
                {{ group.is_active ? 'Activo' : 'Inactivo' }}
              </span>
              <!-- Action dropdown -->
              <DropdownMenu
                :id="`modifier-group-${group.id}`"
                button-label="Group actions"
                width="md"
              >
                <DropdownMenuItem
                  :icon="PencilIcon"
                  variant="default"
                  @click="$emit('edit', group)"
                >
                  Editar
                </DropdownMenuItem>
                
                <DropdownMenuItem
                  :icon="ArrowPathIcon"
                  variant="default"
                  :is-loading="loading"
                  @click="$emit('toggle', group)"
                >
                  {{ group.is_active ? 'Desactivar' : 'Activar' }}
                </DropdownMenuItem>
                
                <DropdownMenuDivider />
                
                <DropdownMenuItem
                  :icon="TrashIcon"
                  variant="danger"
                  @click="$emit('delete', group)"
                >
                  Eliminar
                </DropdownMenuItem>
              </DropdownMenu>
            </div>
          </div>

          <!-- Info badges -->
          <div class="flex flex-wrap gap-2 text-xs">
            <span class="inline-flex items-center px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
              {{ group.options?.length || 0 }} opciones
            </span>
            <span class="inline-flex items-center px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
              {{ group.min_select_default ?? 0 }}-{{ group.max_select_default ?? 1 }}
            </span>
            <span
              v-if="group.required_default"
              class="inline-flex items-center px-2 py-1 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-200"
            >
              Requerido
            </span>
          </div>

          <!-- Options preview -->
          <div
            v-if="group.options && group.options.length > 0"
            class="space-y-1"
          >
            <div class="flex flex-wrap gap-1">
              <span
                v-for="option in group.options.slice(0, 2)"
                :key="option.id || option.name"
                class="inline-flex px-2 py-1 text-xs rounded bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600"
              >
                {{ option.name }}
                <span
                  v-if="option.price_delta && option.price_delta > 0"
                  class="ml-1 text-gray-500"
                >
                  +${{ option.price_delta }}
                </span>
              </span>
              <span
                v-if="group.options.length > 2"
                class="inline-flex px-2 py-1 text-xs rounded bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600"
              >
                +{{ group.options.length - 2 }}
              </span>
            </div>
          </div>
        </div>

        <!-- Desktop: Horizontal layout -->
        <div class="hidden sm:block">
          <div class="flex items-start justify-between gap-4">
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-3">
                <h3 class="text-sm font-semibold text-gray-900 dark:text-white truncate">
                  {{ group.name }}
                </h3>
                <span
                  class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium flex-shrink-0"
                  :class="group.is_active ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-200' : 'bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-300'"
                >
                  {{ group.is_active ? 'Activo' : 'Inactivo' }}
                </span>
              </div>
              
              <p
                v-if="group.description"
                class="text-sm text-gray-600 dark:text-gray-400 mt-1"
              >
                {{ group.description }}
              </p>
              
              <div class="mt-2 flex flex-wrap gap-4 text-xs text-gray-500 dark:text-gray-400">
                <span>{{ group.options?.length || 0 }} opciones</span>
                <span>Min: {{ group.min_select_default ?? 0 }}</span>
                <span>Max: {{ group.max_select_default ?? 1 }}</span>
                <span>{{ group.required_default ? 'Requerido' : 'Opcional' }}</span>
              </div>

              <div
                v-if="group.options && group.options.length > 0"
                class="mt-2"
              >
                <div class="flex flex-wrap gap-1">
                  <span
                    v-for="option in group.options.slice(0, 3)"
                    :key="option.id || option.name"
                    class="inline-flex px-2 py-1 text-xs rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                  >
                    {{ option.name }}
                    <span
                      v-if="option.price_delta && option.price_delta > 0"
                      class="ml-1 text-gray-500"
                    >
                      +${{ option.price_delta }}
                    </span>
                  </span>
                  <span
                    v-if="group.options.length > 3"
                    class="inline-flex px-2 py-1 text-xs rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300"
                  >
                    +{{ group.options.length - 3 }}
                  </span>
                </div>
              </div>
            </div>

            <div class="flex flex-wrap gap-2 flex-shrink-0">
              <DropdownMenu
                :id="`modifier-group-${group.id}`"
                button-label="Group actions"
                width="md"
              >
                <DropdownMenuItem
                  :icon="PencilIcon"
                  variant="default"
                  @click="$emit('edit', group)"
                >
                  Editar
                </DropdownMenuItem>
                
                <DropdownMenuItem
                  :icon="ArrowPathIcon"
                  variant="default"
                  :is-loading="loading"
                  @click="$emit('toggle', group)"
                >
                  {{ group.is_active ? 'Desactivar' : 'Activar' }}
                </DropdownMenuItem>
                
                <DropdownMenuDivider />
                
                <DropdownMenuItem
                  :icon="TrashIcon"
                  variant="danger"
                  @click="$emit('delete', group)"
                >
                  Eliminar
                </DropdownMenuItem>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Card View -->
    <div
      v-else
      class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
    >
      <ModifierGroupCard
        v-for="group in groups"
        :key="group.id"
        :group="group"
        @edit="$emit('edit', group)"
        @toggle="$emit('toggle', group)"
        @delete="$emit('delete', group)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import type { ModifierGroup } from '@/types/menu';
import DropdownMenu from '@/components/ui/DropdownMenu.vue';
import DropdownMenuItem from '@/components/ui/DropdownMenuItem.vue';
import DropdownMenuDivider from '@/components/ui/DropdownMenuDivider.vue';
import ModifierGroupCard from '@/components/modifiers/ModifierGroupCard.vue';
import { PencilIcon, ArrowPathIcon, TrashIcon } from '@heroicons/vue/24/outline';
import LoadingSpinner from '@/components/base/LoadingSpinner.vue';
import EmptyState from '@/components/base/EmptyState.vue';

defineProps<{
  groups: ModifierGroup[];
  loading: boolean;
  viewMode?: 'list' | 'card';
}>();

const emit = defineEmits<{
  edit: [group: ModifierGroup];
  toggle: [group: ModifierGroup];
  delete: [group: ModifierGroup];
}>();

const openDropdownId = ref<number | null>(null);
const dropdownRef = ref<HTMLElement | null>(null);

const toggleDropdown = (groupId: number) => {
  openDropdownId.value = openDropdownId.value === groupId ? null : groupId;
};

const handleAction = (action: 'edit' | 'toggle' | 'delete', group: ModifierGroup) => {
  // Close dropdown
  openDropdownId.value = null;
  
  // Emit the corresponding action
  if (action === 'edit') {
    emit('edit', group);
  } else if (action === 'toggle') {
    emit('toggle', group);
  } else if (action === 'delete') {
    emit('delete', group);
  }
};

// Close dropdown when clicking outside
const handleClickOutside = (event: MouseEvent) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    openDropdownId.value = null;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>
